import { useEffect, useRef, useState } from "react";
import api, { formatApiError, resolveImageUrl } from "@/lib/api";
import { useSettings, SETTINGS_FALLBACK } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, Palette, Type, Image as ImageIcon, Phone, BarChart3, RotateCcw, Wrench, Plus, Trash2, Database, FileSignature } from "lucide-react";

const SECTIONS = [
  {
    id: "brand",
    title: "Brand & hero copy",
    icon: <Type className="w-4 h-4" />,
    fields: [
      { k: "site_name", label: "Site name (brand)", type: "input" },
      { k: "tagline", label: "Header tagline / overline", type: "input" },
      { k: "hero_headline_1", label: "Hero — line 1", type: "input" },
      { k: "hero_headline_2", label: "Hero — line 2 (before accent)", type: "input" },
      { k: "hero_headline_accent", label: "Hero — accent word", type: "input" },
      { k: "hero_headline_3", label: "Hero — line 3 (after accent)", type: "input" },
      { k: "hero_subtitle", label: "Hero — subtitle", type: "textarea" },
      { k: "about_intro", label: "About page intro", type: "textarea" },
    ],
  },
  {
    id: "maintenance",
    title: "Maintenance mode",
    icon: <Wrench className="w-4 h-4" />,
    fields: [
      { k: "maintenance_mode", label: "Maintenance mode (on / off)", type: "toggle" },
      { k: "maintenance_title", label: "Maintenance page title", type: "input" },
      { k: "maintenance_message", label: "Maintenance page message", type: "textarea" },
      { k: "maintenance_whitelist_ips", label: "Whitelisted IPs (one per line)", type: "iplist" },
    ],
  },
  {
    id: "design",
    title: "Design",
    icon: <Palette className="w-4 h-4" />,
    fields: [
      { k: "accent_color", label: "Accent color (HEX)", type: "color" },
      { k: "primary_color", label: "Primary text / button color (HEX)", type: "color" },
      { k: "logo_url", label: "Logo image (transparent PNG/SVG recommended)", type: "image" },
      { k: "hero_image_url", label: "Hero background image URL", type: "image" },
      { k: "about_image_url", label: "About page image URL", type: "image" },
      { k: "category_antiaging_image", label: "Home — Anti-aging category image", type: "image" },
      { k: "category_weightloss_image", label: "Home — Weight Loss category image", type: "image" },
      { k: "category_fitness_image", label: "Home — Fitness category image", type: "image" },
    ],
  },
  {
    id: "stats",
    title: "Hero stats",
    icon: <BarChart3 className="w-4 h-4" />,
    fields: [
      { k: "stat_active_count", label: "Stat 1 — value (e.g. 23)", type: "input" },
      { k: "stat_active_label", label: "Stat 1 — label", type: "input" },
      { k: "stat_lab_tested", label: "Stat 2 — value (e.g. 100%)", type: "input" },
      { k: "stat_lab_tested_label", label: "Stat 2 — label", type: "input" },
      { k: "rx_review_sla", label: "Stat 3 — Rx review SLA (e.g. 48h)", type: "input" },
      { k: "shipping_zones", label: "Stat 4 — shipping zones (e.g. EU/US)", type: "input" },
    ],
  },
  {
    id: "contact",
    title: "Contact & social",
    icon: <Phone className="w-4 h-4" />,
    fields: [
      { k: "contact_email", label: "Contact email", type: "input" },
      { k: "contact_phone", label: "Contact phone", type: "input" },
      { k: "address", label: "Address", type: "textarea" },
      { k: "social_instagram", label: "Instagram URL", type: "input" },
      { k: "social_twitter", label: "Twitter / X URL", type: "input" },
      { k: "social_facebook", label: "Facebook URL", type: "input" },
      { k: "footer_note", label: "Footer note", type: "textarea" },
    ],
  },
  {
    id: "consent",
    title: "Patient consent form",
    icon: <FileSignature className="w-4 h-4" />,
    fields: [
      { k: "consent_title", label: "Form title (shown to patient)", type: "input" },
      { k: "consent_body", label: "Contract / agreement text", type: "textarea", rows: 14 },
      { k: "consent_recipient_email", label: "Recipient email (where signed PDFs are sent)", type: "input" },
      { k: "consent_sender_email", label: "Sender email (leave blank to use platform default)", type: "input" },
    ],
  },
  {
    id: "backup",
    title: "Backup & restore",
    icon: <Database className="w-4 h-4" />,
    fields: [
      { k: "__backup_tools", label: "Your data backups", type: "backup" },
    ],
  },
  {
    id: "migration",
    title: "Data migration",
    icon: <Database className="w-4 h-4" />,
    fields: [
      { k: "__import_snapshot", label: "Import bundled snapshot", type: "import" },
      { k: "__fix_image_urls", label: "Fix legacy image URLs", type: "fixurls" },
    ],
  },
];

function ColorField({ value, onChange, testId }) {
  const safe = /^#[0-9a-fA-F]{6}$/.test(value || "") ? value : "#000000";
  return (
    <div className="mt-2 flex gap-2 items-center">
      <input
        type="color"
        value={safe}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-14 cursor-pointer rounded-md border border-border bg-white"
        data-testid={`${testId}-picker`}
      />
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 font-mono uppercase"
        placeholder="#0D9488"
        data-testid={testId}
      />
    </div>
  );
}

function ImageField({ value, onChange, testId }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="mt-2 space-y-2">
      <div className="flex gap-2">
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… or upload"
          className="flex-1 h-10"
          data-testid={testId}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={upload}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testId}-upload`}
          className="h-10"
        >
          <Upload className="w-4 h-4 mr-2" /> {uploading ? "…" : "Upload"}
        </Button>
      </div>
      {value && (
        <img
          src={resolveImageUrl(value)}
          alt=""
          className="h-24 w-full object-cover border border-border bg-secondary"
        />
      )}
    </div>
  );
}

function IpListField({ value, onChange, testId }) {
  const [myIp, setMyIp] = useState(null);
  useEffect(() => {
    api.get("/admin/my-ip").then((r) => setMyIp(r.data.ip)).catch(() => {});
    // Fetch once on mount; `api` is stable, setMyIp is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ips = String(value || "")
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);

  const update = (next) => onChange(next.join("\n"));
  const add = (ip) => {
    const trimmed = ip.trim();
    if (!trimmed || ips.includes(trimmed)) return;
    update([...ips, trimmed]);
  };
  const remove = (ip) => update(ips.filter((i) => i !== ip));
  const [input, setInput] = useState("");

  return (
    <div className="mt-2 space-y-3">
      {myIp && (
        <div className="flex items-center justify-between flex-wrap gap-2 border border-dashed border-border bg-secondary/40 px-3 py-2 text-xs">
          <div>
            <span className="text-muted-foreground">Your current IP: </span>
            <span className="font-mono font-semibold">{myIp}</span>
          </div>
          <button
            type="button"
            onClick={() => add(myIp)}
            disabled={ips.includes(myIp)}
            data-testid={`${testId}-add-mine`}
            className="text-xs underline text-[hsl(var(--accent))] disabled:opacity-50 disabled:no-underline"
          >
            {ips.includes(myIp) ? "✓ already added" : "Add to whitelist"}
          </button>
        </div>
      )}
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); add(input); setInput(""); }
          }}
          placeholder="e.g. 200.55.12.43"
          className="h-10 font-mono"
          data-testid={`${testId}-input`}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => { add(input); setInput(""); }}
          className="h-10"
          data-testid={`${testId}-add`}
        >
          <Plus className="w-4 h-4 mr-2" /> Add
        </Button>
      </div>
      {ips.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">
          No IPs whitelisted. Only the admin (logged in) will bypass maintenance.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {ips.map((ip) => (
            <span
              key={ip}
              className="inline-flex items-center gap-2 px-2 py-1 bg-secondary border border-border rounded-md text-xs font-mono"
              data-testid={`${testId}-chip-${ip.replace(/[^\w]/g, "_")}`}
            >
              {ip}
              <button
                type="button"
                onClick={() => remove(ip)}
                aria-label={`Remove ${ip}`}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ToggleField({ value, onChange, testId, label }) {
  const isOn = String(value || "off").toLowerCase() === "on";
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => onChange(isOn ? "off" : "on")}
        data-testid={testId}
        className={`relative inline-flex h-9 w-20 items-center rounded-md border transition-colors ${
          isOn
            ? "bg-emerald-500 border-emerald-600 text-white"
            : "bg-secondary border-border text-muted-foreground"
        }`}
      >
        <span className={`absolute top-1 h-6 w-9 rounded bg-white shadow transition-transform ${
          isOn ? "translate-x-10" : "translate-x-1"
        }`} />
        <span className="absolute inset-0 flex items-center justify-center font-mono text-[10px] tracking-widest pointer-events-none">
          {isOn ? "ON" : "OFF"}
        </span>
      </button>
      <p className="text-xs text-muted-foreground mt-2">
        {isOn
          ? "⚠️ Site is in maintenance — visitors see the maintenance page. Admins (logged in) and whitelisted IPs bypass it."
          : "Site is live and accessible to all visitors."}
      </p>
    </div>
  );
}

function BackupTools() {
  const fileRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/export-snapshot", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      const d = new Date();
      const stamp = d.toISOString().slice(0, 16).replace(/[:-]/g, "");
      a.href = url;
      a.download = `peptideseries-snapshot-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  const emailBackup = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/admin/email-snapshot");
      toast.success(`Backup emailed to ${data.recipient}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEmailing(false);
    }
  };

  const uploadBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const msg = overwrite
      ? "Restore from this file AND overwrite existing items with same IDs?"
      : "Restore from this file (existing items will be kept, only new ones added)?";
    if (!window.confirm(msg)) {
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/upload-snapshot?overwrite_existing=${overwrite}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setLastResult(data);
      toast.success("Snapshot restored");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="mt-2 space-y-4">
      <div className="border border-border bg-secondary/40 p-4 text-sm">
        <div className="font-display font-semibold mb-1">Why this matters</div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Every time you change products, categories or site settings, download or email yourself a
          fresh backup. If your data is ever lost during a redeploy, you can restore everything in
          one click from the JSON file.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={downloadBackup}
          disabled={downloading}
          className="rounded-md h-10"
          data-testid="backup-download"
        >
          <Database className="w-4 h-4 mr-2" />
          {downloading ? "Preparing…" : "Download backup"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={emailBackup}
          disabled={emailing}
          className="rounded-md h-10"
          data-testid="backup-email"
        >
          {emailing ? "Sending…" : "Email me a backup"}
        </Button>
      </div>

      <div className="border-t border-border pt-4">
        <div className="font-display font-semibold text-sm mb-2">Restore from file</div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => setOverwrite(e.target.checked)}
            data-testid="backup-overwrite"
          />
          Overwrite items with same IDs (settings always merge)
        </label>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={uploadBackup}
        />
        <Button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="rounded-md h-10"
          data-testid="backup-upload"
        >
          <Upload className="w-4 h-4 mr-2" />
          {uploading ? "Restoring…" : "Upload snapshot file"}
        </Button>
        {lastResult && (
          <div className="mt-3 border border-emerald-200 bg-emerald-50 p-3 text-xs">
            <div className="font-display font-semibold text-emerald-800">Restore complete</div>
            <ul className="mt-2 space-y-1 text-emerald-900">
              <li>Categories: +{lastResult.categories.added} added, ~{lastResult.categories.updated} updated, −{lastResult.categories.skipped} skipped</li>
              <li>Products: +{lastResult.products.added} added, ~{lastResult.products.updated} updated, −{lastResult.products.skipped} skipped</li>
              <li>Settings keys merged: {lastResult.settings_keys_merged}</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function ImportSnapshotField() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [overwrite, setOverwrite] = useState(false);

  useEffect(() => {
    api.get("/admin/import-snapshot/preview")
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ available: false }))
      .finally(() => setLoading(false));
    // Fetch once on mount; api is a module singleton, state setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runImport = async () => {
    const confirmMsg = overwrite
      ? "Import snapshot AND overwrite existing categories/products with same IDs?"
      : "Import snapshot (existing items with same IDs will be skipped)?";
    if (!window.confirm(confirmMsg)) return;
    setImporting(true);
    try {
      const { data } = await api.post(`/admin/import-snapshot?overwrite_existing=${overwrite}`);
      setResult(data);
      toast.success("Snapshot imported");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground mt-2">Loading…</div>;
  }
  if (!info?.available) {
    return (
      <div className="mt-2 border border-dashed border-border bg-secondary/40 p-4 text-sm text-muted-foreground">
        No snapshot file bundled with this deployment.
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-3">
      <div className="border border-border bg-secondary/40 p-4 text-sm">
        <div className="font-display font-semibold">Bundled snapshot v{info.version}</div>
        <ul className="mt-2 grid grid-cols-3 gap-2 text-xs">
          <li className="border border-border bg-white p-2">
            <div className="font-mono text-lg">{info.counts.categories}</div>
            <div className="overline text-[9px]">categories</div>
          </li>
          <li className="border border-border bg-white p-2">
            <div className="font-mono text-lg">{info.counts.products}</div>
            <div className="overline text-[9px]">products</div>
          </li>
          <li className="border border-border bg-white p-2">
            <div className="font-mono text-lg">{info.counts.settings_keys}</div>
            <div className="overline text-[9px]">settings keys</div>
          </li>
        </ul>
      </div>

      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={overwrite}
          onChange={(e) => setOverwrite(e.target.checked)}
          data-testid="snapshot-overwrite"
        />
        Overwrite existing categories/products with same IDs (settings always merge)
      </label>

      <Button
        type="button"
        onClick={runImport}
        disabled={importing}
        data-testid="snapshot-import-btn"
        className="rounded-md h-10"
      >
        <Database className="w-4 h-4 mr-2" />
        {importing ? "Importing…" : "Run import"}
      </Button>

      {result && (
        <div className="border border-emerald-200 bg-emerald-50 p-3 text-xs">
          <div className="font-display font-semibold text-emerald-800">Import complete</div>
          <ul className="mt-2 space-y-1 text-emerald-900">
            <li>Categories: +{result.categories.added} added, ~{result.categories.updated} updated, −{result.categories.skipped} skipped</li>
            <li>Products: +{result.products.added} added, ~{result.products.updated} updated, −{result.products.skipped} skipped</li>
            <li>Settings keys merged: {result.settings_keys_merged}</li>
          </ul>
        </div>
      )}
    </div>
  );
}

function FixImageUrlsField() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/fix-image-urls");
      setResult(data);
      toast.success("Image URLs cleaned");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="mt-2 space-y-3">
      <p className="text-xs text-muted-foreground">
        Rewrites any absolute preview/legacy image URLs (e.g. <code>https://…emergent…/api/media/…</code>)
        into host-relative <code>/api/media/…</code> paths. Use this if you imported a snapshot and product/category
        images don&apos;t load on the live domain. Safe to run multiple times.
      </p>
      <Button
        type="button"
        onClick={run}
        disabled={running}
        data-testid="fix-image-urls-btn"
        className="rounded-md h-10"
        variant="outline"
      >
        {running ? "Running…" : "Fix image URLs"}
      </Button>
      {result && (
        <div className="border border-emerald-200 bg-emerald-50 p-3 text-xs">
          <div className="font-display font-semibold text-emerald-800">Cleanup complete</div>
          <ul className="mt-2 space-y-1 text-emerald-900">
            <li>Categories fixed: {result.categories_fixed}</li>
            <li>Products fixed: {result.products_fixed}</li>
            <li>Settings keys fixed: {result.settings_keys_fixed}</li>
          </ul>
        </div>
      )}
    </div>
  );
}

export default function AdminSettings() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);

  // Fetch full admin settings (incl. whitelist IPs which the public endpoint hides)
  useEffect(() => {
    api.get("/admin/settings")
      .then((r) => setForm({ ...SETTINGS_FALLBACK, ...settings, ...r.data }))
      .catch(() => setForm({ ...SETTINGS_FALLBACK, ...settings }));
  }, [settings]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
      await refresh();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset ALL settings (brand, design, stats, contact) to defaults?")) return;
    setForm(SETTINGS_FALLBACK);
    await api.put("/admin/settings", SETTINGS_FALLBACK);
    await refresh();
    toast.success("Reset to defaults");
  };

  const resetDesign = async () => {
    if (!window.confirm("Reset only design (colors + images) to defaults?")) return;
    const designKeys = ["accent_color", "primary_color", "hero_image_url", "about_image_url"];
    const next = { ...form };
    designKeys.forEach((k) => { next[k] = SETTINGS_FALLBACK[k]; });
    setForm(next);
    await api.put("/admin/settings", next);
    await refresh();
    toast.success("Design reset");
  };

  const section = SECTIONS.find((s) => s.id === activeSection);

  return (
    <div data-testid="admin-settings" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Site configuration</div>
          <h2 className="font-display text-2xl font-semibold mt-1">Settings</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Edit brand, design, stats, contact and footer. Design changes apply live.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="ghost" onClick={resetDesign} data-testid="settings-reset-design">
            <RotateCcw className="w-4 h-4 mr-2" /> Reset design
          </Button>
          <Button variant="outline" onClick={reset} data-testid="settings-reset">Reset all</Button>
          <Button onClick={save} disabled={saving} className="rounded-md" data-testid="settings-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-4 lg:gap-6">
        <aside className="border border-border bg-white">
          <nav className="flex flex-row lg:flex-col overflow-x-auto no-scrollbar">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                data-testid={`settings-section-${s.id}`}
                className={`flex items-center gap-2 px-4 h-12 text-sm whitespace-nowrap lg:border-b border-border lg:last:border-b-0 text-left shrink-0 ${
                  activeSection === s.id
                    ? "bg-foreground text-white"
                    : "hover:bg-secondary text-muted-foreground"
                }`}
              >
                {s.icon} {s.title}
              </button>
            ))}
          </nav>
        </aside>

        <div className="border border-border bg-white p-4 sm:p-6 grid sm:grid-cols-2 gap-5">
          {section.fields.map((f) => (
            <div key={f.k} className={["textarea", "image", "toggle", "iplist", "import", "fixurls"].includes(f.type) ? "sm:col-span-2" : ""}>
              <Label htmlFor={f.k} className="text-xs">{f.label}</Label>
              {f.type === "textarea" && (
                <Textarea
                  id={f.k} rows={f.rows || 3}
                  value={form[f.k] ?? ""}
                  onChange={(e) => update(f.k, e.target.value)}
                  className="mt-2"
                  data-testid={`settings-${f.k}`}
                />
              )}
              {f.type === "input" && (
                <Input
                  id={f.k}
                  value={form[f.k] ?? ""}
                  onChange={(e) => update(f.k, e.target.value)}
                  className="mt-2 h-10"
                  data-testid={`settings-${f.k}`}
                />
              )}
              {f.type === "color" && (
                <ColorField
                  value={form[f.k] ?? "#000000"}
                  onChange={(v) => update(f.k, v)}
                  testId={`settings-${f.k}`}
                />
              )}
              {f.type === "image" && (
                <ImageField
                  value={form[f.k] ?? ""}
                  onChange={(v) => update(f.k, v)}
                  testId={`settings-${f.k}`}
                />
              )}
              {f.type === "toggle" && (
                <ToggleField
                  value={form[f.k] ?? "off"}
                  onChange={(v) => update(f.k, v)}
                  testId={`settings-${f.k}`}
                  label={f.label}
                />
              )}
              {f.type === "iplist" && (
                <IpListField
                  value={form[f.k] ?? ""}
                  onChange={(v) => update(f.k, v)}
                  testId={`settings-${f.k}`}
                />
              )}
              {f.type === "import" && <ImportSnapshotField />}
              {f.type === "backup" && <BackupTools />}
              {f.type === "fixurls" && <FixImageUrlsField />}
            </div>
          ))}
        </div>
      </div>

      {error && <div className="text-sm text-destructive">{error}</div>}
    </div>
  );
}
